<script lang="ts" setup>import { computed as _computed } from 'vue';

import Envelope from './Envelope.vue'

withDefaults(defineProps<{
  play: boolean
  fadeOut: boolean
  senderName?: string
  recipientName?: string
  previewImageUrl?: string | null
  // Full-bleed face, when the card has one — shown in place of the portrait
  // `previewImageUrl` so the in-envelope card matches the reading-stage cover.
  fullPageImageUrl?: string | null
}>(), {  })

const faceImageUrl = _computed(() => __props.fullPageImageUrl || __props.previewImageUrl || null)
</script>

<template>
  <Envelope
    :play="play"
    :fadeOut="fadeOut"
    :senderName="senderName"
    :recipientName="recipientName"
  >
    <!--
      The card is the slotted contents of the envelope. It sits sandwiched
      between env-back (z low) and env-front (z high) — see Envelope.vue
      for the layering. The card has its own rotateY(180) so that — when
      the envelope itself flips by 180° — the compound rotation lands at 0°
      and faces the viewer. (Before the flip it's facing away and hidden by
      backface-visibility, which is fine because env-front covers it.)
      The card has NO slide-down animation, so when the two env-faces
      translate off the screen, the card stays put.
      The card has no shadow initially — while sitting inside the envelope
      a shadow would look wrong (it's nested between paper walls). On
      fade-out (signalled by the `fade-out` class) the shadow animates in
      to its full value, ending at the same shadow the Cover component
      shows at handoff so the transition is seamless.
    -->
    <div class="card" :class="{ 'fade-out': fadeOut }">
      <img
        v-if="faceImageUrl"
        :src="faceImageUrl"
        alt=""
        class="card-cover-image"
      >
      <template v-else>
        <h1>A gift for you</h1>
        <p v-if="senderName">From {{ senderName }}</p>
        <div class="heart">♥</div>
      </template>
    </div>
  </Envelope>
</template>

<style scoped lang="scss">
.card {
  align-items: center;
  backface-visibility: hidden;
  background: var(--card-color);
  clip-path: inset(-200px -200px calc(var(--card-height) - var(--env-height)) -200px);
  display: flex;
  flex-direction: column;

  // Card always renders at its full `--card-height`. The in-envelope
  // view is controlled by `clip-path` below — initially the bottom
  // (--card-height - --env-height) is clipped away, so what's visible
  // matches the envelope's bounds. On fade-out the clip animates open
  // to reveal the rest of the card, which replaces what used to be a
  // `height` tween. `height` is layout-triggering and forces a reflow
  // every frame; `clip-path` is GPU-composited and doesn't touch
  // layout, so the reveal stays smooth on mobile alongside the
  // envelope's concurrent slide / color / flap-fade animations.
  // NB: the inset values use a `-200px` margin on the sides that
  // aren't clipping the card body. `clip-path` clips both content
  // AND outset box-shadows to the clip region, so a tight `inset(0)`
  // would swallow the shadow that fades in over the same animation.
  // The negative margin gives box-shadow ~200px of room to render
  // outside the card's bounds — well beyond the shadow's reach — so
  // the depth lands intact at the end of the reveal.
  height: var(--card-height);
  justify-content: center;
  left: 50%;
  overflow: hidden;
  position: absolute;
  text-align: center;

  // Top is anchored to the envelope's top, so the in-envelope view
  // shows image rows 0..--env-height. The card then animates UP by
  // half the height difference during fade-out (see `card-reveal`),
  // so its CENTER lands at the envelope's center — matching where the
  // reading-stage cover sits (centered at scene center − navbar/2).
  // Without this lift the card visibly jumps down when the reading
  // stage takes over.
  top: calc(50% - var(--env-height) / 2);
  transform: translateX(-50%) translateY(0) rotateY(180deg) translateZ(0);
  width: var(--card-width);
  z-index: 20;
}

// On fade-out the card "grows" by opening the clip-path while it
// simultaneously translates up so its center lands at the envelope's
// center (handoff position for the reading stage). Net visual: the
// card extends symmetrically — top moves up, bottom moves down —
// while the shadow fades in. Envelope slide-down lives in Envelope.vue.
.card.fade-out {
  animation: card-reveal 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes card-reveal {
  from {
    box-shadow: 0 0 0 rgb(0 0 0 / 0%);
    clip-path: inset(-200px -200px calc(var(--card-height) - var(--env-height)) -200px);
    transform: translateX(-50%) translateY(0) rotateY(180deg) translateZ(0);
  }

  to {
    box-shadow: var(--card-envelope-box-shadow);
    clip-path: inset(-200px);
    transform:
      translateX(-50%)
      translateY(calc((var(--env-height) - var(--card-height)) / 2))
      rotateY(180deg)
      translateZ(0);
  }
}

.card h1 {
  color: var(--text-dark);
  font-size: 28px;
  font-weight: normal;
  letter-spacing: 1px;
  margin-bottom: 15px;
}

.card p {
  color: var(--text-light);
  font-size: 14px;
  line-height: 1.6;
  max-width: 80%;
}

.card .heart {
  color: var(--stamp-color);
  font-size: 22px;
  margin-top: 15px;
}

.card-cover-image {
  display: block;
  height: var(--card-height);
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
}
</style>
